import { useCallback, useEffect, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import {
  enrichMissingAirlines,
  getPendingAirlines,
  approveAirlineSuggestion,
  rejectAirlineSuggestion,
  approveHighConfidence
} from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import './Admin.css';

function diffCellClass(current, pending) {
  if (pending == null) return 'admin-diff-unchanged';
  if (current == null || current === '') return 'admin-diff-new';
  return String(current) === String(pending) ? 'admin-diff-unchanged' : 'admin-diff-changed';
}

function formatNum(v) {
  if (v == null || Number.isNaN(v)) return '';
  return Number(v).toFixed(3);
}

function AdminAirlineSuggestions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [running, setRunning] = useState(false);
  const [batchLimit, setBatchLimit] = useState(20);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const resp = await getPendingAirlines({ limit: 200 }, token);
      if (isApiSuccess(resp)) {
        setRows(resp.rows || []);
      } else {
        setError(getFirstApiError(resp) || 'Failed to load pending suggestions');
      }
    } catch (e) {
      setError('Failed to load pending suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runEnrichment = async () => {
    setRunning(true);
    setActionMsg(null);
    try {
      const token = getAccessToken();
      const resp = await enrichMissingAirlines({ limit: batchLimit }, token);
      if (isApiSuccess(resp)) {
        setActionMsg({
          type: 'success',
          text: `Processed ${resp.processed}, matched ${resp.matched}, not found ${resp.notFound}.`
        });
        load();
      } else {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Enrichment failed' });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Enrichment failed' });
    } finally {
      setRunning(false);
    }
  };

  const approve = async (icaoCode, uploadLogoToS3) => {
    try {
      const token = getAccessToken();
      const resp = await approveAirlineSuggestion({ icaoCode, uploadLogoToS3 }, token);
      if (isApiSuccess(resp) && resp.approved) {
        setActionMsg({
          type: 'success',
          text: `Approved ${icaoCode}${resp.logoUrl ? ` (logo → S3)` : ''}`
        });
        load();
      } else {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Approve failed' });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Approve failed' });
    }
  };

  const approveAllHigh = async (uploadLogos) => {
    const highCount = rows.filter(r => (r.pendingConfidence || '').toUpperCase() === 'HIGH').length;
    if (highCount === 0) {
      setActionMsg({ type: 'error', text: 'No HIGH-confidence suggestions to approve.' });
      return;
    }
    const promptText = `Approve all ${highCount} HIGH-confidence suggestions${uploadLogos ? ' and upload logos to S3' : ''}?`;
    if (!window.confirm(promptText)) return;
    try {
      const token = getAccessToken();
      const resp = await approveHighConfidence({ uploadLogosToS3: !!uploadLogos }, token);
      if (isApiSuccess(resp)) {
        setActionMsg({
          type: 'success',
          text: `Approved ${resp.approvedCount} (logos uploaded: ${resp.logosUploaded || 0}). ${resp.skippedCount} non-HIGH skipped.`
        });
        load();
      } else {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Bulk approve failed' });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Bulk approve failed' });
    }
  };

  const reject = async (icaoCode) => {
    if (!window.confirm(`Reject pending suggestion for ${icaoCode}?`)) return;
    try {
      const token = getAccessToken();
      const resp = await rejectAirlineSuggestion({ icaoCode }, token);
      if (isApiSuccess(resp) && resp.rejected) {
        setActionMsg({ type: 'success', text: `Rejected ${icaoCode}` });
        load();
      } else {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Reject failed' });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Reject failed' });
    }
  };

  if (loading) return <div className="admin-loading">Loading pending suggestions...</div>;
  if (error) {
    return (
      <div className="admin-error">
        <p>{error}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-table-container">
      <div className="admin-toolbar">
        <label>
          Batch size:
          <input
            type="number"
            min={1}
            max={100}
            value={batchLimit}
            onChange={e => setBatchLimit(parseInt(e.target.value, 10) || 20)}
            style={{ width: 60 }}
          />
        </label>
        <button onClick={runEnrichment} disabled={running}>
          {running ? 'Running...' : 'Run enrichment for top N missing codes'}
        </button>
        <button onClick={() => approveAllHigh(true)} disabled={running}>
          Approve all HIGH + logos to S3
        </button>
        <button onClick={() => approveAllHigh(false)} disabled={running}>
          Approve all HIGH (fields only)
        </button>
        <button onClick={load}>Refresh</button>
        <span className="admin-toolbar-meta">
          {rows.length} pending • HIGH: {rows.filter(r => (r.pendingConfidence || '').toUpperCase() === 'HIGH').length}
          {' '}• MEDIUM: {rows.filter(r => (r.pendingConfidence || '').toUpperCase() === 'MEDIUM').length}
          {' '}• LOW: {rows.filter(r => (r.pendingConfidence || '').toUpperCase() === 'LOW').length}
        </span>
      </div>

      {actionMsg && (
        <div className={`admin-action-message admin-action-${actionMsg.type}`}>
          {actionMsg.text}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="admin-loading">
          No pending suggestions. Click "Run enrichment" to populate from top missing codes.
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ICAO</th>
              <th>Field</th>
              <th>Current</th>
              <th>Proposed</th>
              <th>Source</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const fields = [
                { label: 'IATA',         current: row.currentIataCode,    pending: row.pendingIataCode },
                { label: 'Name',         current: row.currentName,        pending: row.pendingName },
                { label: 'Country',      current: row.currentCountry,     pending: row.pendingCountry },
                { label: 'Country code', current: row.currentCountryCode, pending: row.pendingCountryCode },
                { label: 'Callsign',     current: row.currentCallsign,    pending: row.pendingCallsign },
                {
                  label: 'HQ lat/lon',
                  current: (row.currentLatitude != null && row.currentLongitude != null)
                    ? `${formatNum(row.currentLatitude)}, ${formatNum(row.currentLongitude)}` : '',
                  pending: (row.pendingLatitude != null && row.pendingLongitude != null)
                    ? `${formatNum(row.pendingLatitude)}, ${formatNum(row.pendingLongitude)}` : ''
                },
                { label: 'Logo',         current: row.currentLogoUrl,     pending: row.pendingLogoUrl, isLogo: true }
              ];
              return (
                <>
                  {fields.map((f, idx) => (
                    <tr key={`${row.icaoCode}:${f.label}`} className="admin-suggestion-row">
                      {idx === 0 ? (
                        <td rowSpan={fields.length + 1} className="admin-suggestion-icao">
                          <strong>{row.icaoCode}</strong>
                        </td>
                      ) : null}
                      <td>{f.label}</td>
                      <td className={diffCellClass(f.current, f.pending)}>
                        {f.isLogo
                          ? (f.current ? <img src={f.current} alt="" style={{ height: 24, maxWidth: 80, objectFit: 'contain' }} /> : <span className="gap">—</span>)
                          : (f.current || <span className="gap">—</span>)}
                      </td>
                      <td className={diffCellClass(f.current, f.pending)}>
                        {f.isLogo
                          ? (f.pending ? <img src={f.pending} alt="" style={{ height: 24, maxWidth: 80, objectFit: 'contain' }} /> : <span className="gap">—</span>)
                          : (f.pending || <span className="gap">—</span>)}
                      </td>
                      <td>{idx === 0
                        ? <span className={`admin-confidence admin-confidence-${(row.pendingConfidence || 'LOW').toLowerCase()}`}>{row.pendingConfidence || '—'}</span>
                        : ''}</td>
                      {idx === 0 ? (
                        <td rowSpan={fields.length + 1}>
                          <div className="admin-suggestion-actions">
                            <button onClick={() => approve(row.icaoCode, !!row.pendingLogoUrl)}>
                              Approve {row.pendingLogoUrl ? '+ logo to S3' : ''}
                            </button>
                            <button onClick={() => approve(row.icaoCode, false)}>
                              Approve fields only
                            </button>
                            <button onClick={() => reject(row.icaoCode)} className="admin-reject">
                              Reject
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                  <tr key={`${row.icaoCode}:notes`} className="admin-suggestion-notes">
                    <td colSpan={4}>
                      <small><strong>Source:</strong> {row.pendingSource || '—'} • <strong>Notes:</strong> {row.pendingNotes || '—'}</small>
                    </td>
                  </tr>
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminAirlineSuggestions;
